'use client';

import Form from 'next/form';
import { useActionState, useEffect } from 'react';
import { useFormStatus } from 'react-dom';
import { toast } from 'sonner';

import CallToAction from '~/components/atoms/CallToAction';
import Checkbox from '~/components/molecules/Checkbox';
import FormInput from '~/components/molecules/FormInput';
import FormSelect from '~/components/molecules/FormSelect';
import { contactFormSubjectOptions } from '~/lib/@types/contact-form';
import {
  sendContactMessage,
  SendContactMessageState,
} from '~/lib/actions/contact';

interface ContactFormClientProps {
  checkboxLabel?: string | null;
}

const ContactFormContents: React.FC<
  ContactFormClientProps & {
    state: SendContactMessageState;
  }
> = ({ checkboxLabel, state }) => {
  const status = useFormStatus();

  useEffect(() => {
    switch (state.status) {
      case 'success': {
        toast.success('Message envoyé avec succès', {
          description: 'Nous reviendrons vers vous dans les plus brefs délais',
          duration: 8000,
          dismissible: true,
        });

        close();

        break;
      }

      case 'error': {
        toast.error('Une erreur est survenue', {
          description: 'Veuillez réessayer plus tard',
          duration: 8000,
          dismissible: true,
        });

        break;
      }
    }
  }, [state.status]);

  return (
    <div className="text-brand-primary flex flex-col items-center gap-y-4">
      <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4">
        <FormInput
          type="text"
          label="Prénom"
          name="first_name"
          autoComplete="given-name"
          className="col-span-2 lg:col-span-1"
          placeholder="Prénom"
          disabled={status.pending}
          required
        />

        <FormInput
          type="text"
          label="Nom"
          name="last_name"
          autoComplete="family-name"
          className="col-span-2 lg:col-span-1"
          placeholder="Nom"
          disabled={status.pending}
          required
        />

        <FormInput
          type="email"
          label="Email"
          name="email"
          autoComplete="email"
          className="col-span-2 lg:col-span-1"
          placeholder="adresse@email.fr"
          disabled={status.pending}
          required
        />

        <FormInput
          type="tel"
          label="Téléphone"
          name="phone"
          autoComplete="home tel"
          className="col-span-2 lg:col-span-1"
          placeholder="01 23 45 67 89"
          disabled={status.pending}
        />

        <FormSelect
          label="Objet"
          name="subject"
          className="col-span-2"
          placeholder="Objet du message"
          options={contactFormSubjectOptions}
          disabled={status.pending}
          withLabelValue
          required
        />

        <FormInput
          type="textarea"
          label="Message"
          name="message"
          className="col-span-2"
          placeholder="Votre message"
          disabled={status.pending}
        />
      </div>

      <div className="flex w-full justify-start">
        <Checkbox
          id="contact-form__gdpr"
          name="contact-form__gdpr"
          required
          html={
            checkboxLabel ||
            'J’accepte les conditions d’utilisation et j’autorise le traitement de mes données dans le seul but d’être recontacté(e) concernant ma demande'
          }
        />
      </div>

      <div className="flex w-full justify-end">
        <CallToAction
          as="button"
          type="submit"
          variant="dark"
          disabled={status.pending}
          loading={status.pending}
        >
          Envoyer le message
        </CallToAction>
      </div>
    </div>
  );
};

const initialState: SendContactMessageState = {
  status: 'idle',
};

export const ContactFormClient: React.FC<ContactFormClientProps> = ({
  checkboxLabel,
}) => {
  const [state, formAction, pending] = useActionState(
    sendContactMessage,
    initialState
  );

  return (
    <Form action={formAction} disabled={pending}>
      <ContactFormContents checkboxLabel={checkboxLabel} state={state} />
    </Form>
  );
};

export default ContactFormClient;
